import React, { Suspense, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Scissors, 
  Sparkles, 
  Award,
  Clock,
  Users,
  BookOpen,
  CheckCircle,
  ArrowLeft,
  ExternalLink,
  GraduationCap,
  TrendingUp,
  Heart,
  Star
} from 'lucide-react';
import { Canvas, useFrame } from "@react-three/fiber";
import {
  OrbitControls,
  useGLTF,
  Stage,
  Environment,
  Center,
  Stars,
  PerspectiveCamera
} from "@react-three/drei";

// Modèle 3D (vous pouvez utiliser le même ou un autre modèle)
useGLTF.preload("/models/head_ring_jewly_hair_pin_retopo_and_hairstyle.glb");

function Model3D() {
  const { scene } = useGLTF("/models/head_ring_jewly_hair_pin_retopo_and_hairstyle.glb");
  const ref = useRef();
  
  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    ref.current.rotation.y = t * 0.15;
  });
  
  return (
    <primitive
      ref={ref}
      object={scene}
      scale={[0.4, 0.4, 0.4]}
      position={[0, -1, 0]}
    />
  );
}

function Scene3D() {
  return (
    <Canvas
      shadows
      className="fixed inset-0 -z-10"
      dpr={[1, 2]}>
      <color attach="background" args={["#1e1e2e"]} />
      <fog attach="fog" args={["#1e1e2e", 5, 15]} />
      <ambientLight intensity={0.5} />
      <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} intensity={1} castShadow />
      
      <Suspense fallback={null}>
        <PerspectiveCamera makeDefault position={[0, 1, 10]} fov={40} />
        
        <Stage environment="city">
          <Center>
            <Model3D />
          </Center>
        </Stage>
        
        <Environment preset="city" />
        <OrbitControls enableZoom={false} enablePan={false} autoRotate autoRotateSpeed={0.3} />
        <Stars count={5000} depth={50} fade speed={1.5} />
      </Suspense>
    </Canvas>
  );
}

const BeauteBienEtre = () => {
  const formations = [
    {
      title: "CAP Métiers de la Coiffure",
      duree: "2 ans",
      niveau: "Niveau 3 (CAP)",
      description: "Formation complète aux techniques de coiffure : coupe, coloration, permanente, brushing et relations clients.",
      competences: [
        "Réalisation de techniques d'hygiène et de soins capillaires",
        "Réalisation de coupes de cheveux",
        "Réalisation de mises en forme temporaire",
        "Réalisation de colorations et d'éclaircissements",
        "Conseil et vente de produits et services"
      ],
      debouches: [
        "Coiffeur/Coiffeuse en salon",
        "Assistant(e) coiffeur/coiffeuse",
        "Coloriste",
        "Poursuite en BP ou Mention Complémentaire"
      ]
    },
    {
      title: "BP Coiffure",
      duree: "2 ans",
      niveau: "Niveau 4 (BAC)",
      description: "Formation approfondie pour devenir un professionnel hautement qualifié et manager d'équipe en salon de coiffure.",
      competences: [
        "Création et réalisation de coupes personnalisées",
        "Techniques avancées de coloration",
        "Gestion et management d'équipe",
        "Création et développement d'une entreprise",
        "Techniques de diagnostic et de conseil personnalisé"
      ],
      debouches: [
        "Responsable de salon",
        "Chef d'entreprise",
        "Formateur en coiffure",
        "Coiffeur hautement qualifié"
      ]
    },
    {
      title: "Mention Complémentaire Coiffure Coupe Couleur",
      duree: "1 an",
      niveau: "Niveau 3 (CAP+1)",
      description: "Spécialisation dans les techniques de coupe et de couleur pour devenir un expert coloriste.",
      competences: [
        "Techniques avancées de coloration",
        "Diagnostic et analyse des cheveux",
        "Techniques de coupe tendance",
        "Conseil personnalisé en couleur",
        "Veille technologique et tendances"
      ],
      debouches: [
        "Coloriste expert",
        "Technicien(ne) coloriste",
        "Coiffeur/Coiffeuse spécialisé(e)",
        "Poursuite en BP Coiffure"
      ]
    },
    {
      title: "CAP Esthétique Cosmétique Parfumerie",
      duree: "2 ans",
      niveau: "Niveau 3 (CAP)",
      description: "Formation aux techniques de soins esthétiques du visage, des mains et des pieds, ainsi qu'aux techniques d'épilation et de maquillage.",
      competences: [
        "Techniques de soins esthétiques du visage",
        "Techniques d'épilation",
        "Techniques de soins des mains et des pieds",
        "Maquillage du visage",
        "Conseil et vente de produits cosmétiques et de prestations esthétiques"
      ],
      debouches: [
        "Esthéticien(ne) en institut de beauté",
        "Conseiller(ère) de vente en parfumerie",
        "Maquilleur/Maquilleuse",
        "Poursuite en BP Esthétique"
      ]
    }
  ];

  const equipements = [
    {
      icon: <Scissors className="w-6 h-6 text-purple-400" />,
      titre: "Salon de Coiffure Professionnel",
      description: "6 bacs à shampoing et 14 postes de travail équipés du matériel professionnel le plus récent"
    },
    {
      icon: <Sparkles className="w-6 h-6 text-pink-400" />,
      titre: "Produits Professionnels",
      description: "Tout le matériel et les produits spécialisés pour découvrir les techniques modernes"
    },
    {
      icon: <Users className="w-6 h-6 text-blue-400" />,
      titre: "Ouvert au Public",
      description: "Salon pédagogique ouvert à la clientèle sur rendez-vous au 02 38 98 05 92"
    }
  ];

  const avantages = [
    "Formation en alternance rémunérée",
    "Équipements professionnels neufs",
    "Formateurs experts du secteur",
    "Pratique sur clientèle réelle",
    "Accompagnement personnalisé",
    "Taux de réussite élevé"
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen py-40 bg-gradient-to-br from-slate-900 to-slate-800"
    >
      {/* Scène 3D en arrière-plan */}
      <div className="fixed inset-0 z-0">
        <Scene3D />
      </div>

      <div className="container mx-auto px-4 mb-32 relative z-10">
        {/* Bouton retour */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <Link to="/">
            <motion.button
              whileHover={{ scale: 1.05, x: -5 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center space-x-2 text-white hover:text-purple-400 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Retour à l'accueil</span>
            </motion.button>
          </Link>
        </motion.div>

        {/* En-tête */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
            className="inline-block p-4 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full mb-6"
          >
            <Scissors className="w-16 h-16 text-purple-400" />
          </motion.div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Pôle Beauté - Bien-être
          </h1>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Découvrez nos formations en coiffure et esthétique, conçues pour faire de vous un professionnel accompli et créatif dans l'univers de la beauté.
          </p>
        </motion.div>

        {/* Section équipements */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Nos Équipements
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {equipements.map((equip, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="bg-slate-800/60 p-6 rounded-lg backdrop-blur-sm border border-purple-500/30 hover:border-purple-400/50 transition-all"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-3 bg-purple-900/30 rounded-full">
                    {equip.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-white">{equip.titre}</h3>
                </div>
                <p className="text-gray-300 text-sm">{equip.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Section formations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Nos Formations
          </h2>

          <div className="space-y-8 max-w-6xl mx-auto">
            {formations.map((formation, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 + index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="bg-slate-800/60 p-8 rounded-xl backdrop-blur-sm border-l-4 border-purple-500 hover:border-pink-500 hover:shadow-2xl hover:shadow-purple-500/20 transition-all"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-4">
                      <GraduationCap className="w-8 h-8 text-purple-400" />
                      <h3 className="text-2xl font-bold text-white">{formation.title}</h3>
                    </div>
                    <p className="text-gray-300 leading-relaxed mb-4">{formation.description}</p>
                  </div>
                  
                  <div className="flex flex-col space-y-2 md:ml-6">
                    <div className="flex items-center space-x-2 bg-purple-900/30 px-4 py-2 rounded-lg">
                      <Clock className="w-4 h-4 text-purple-400" />
                      <span className="text-purple-200 text-sm">{formation.duree}</span>
                    </div>
                    <div className="flex items-center space-x-2 bg-pink-900/30 px-4 py-2 rounded-lg">
                      <Award className="w-4 h-4 text-pink-400" />
                      <span className="text-pink-200 text-sm">{formation.niveau}</span>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                      <BookOpen className="w-5 h-5 mr-2 text-purple-400" />
                      Compétences acquises
                    </h4>
                    <ul className="space-y-2">
                      {formation.competences.map((comp, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-gray-300 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span>{comp}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                      <TrendingUp className="w-5 h-5 mr-2 text-pink-400" />
                      Débouchés professionnels
                    </h4>
                    <ul className="space-y-2">
                      {formation.debouches.map((debouche, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-gray-300 text-sm">
                          <Star className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                          <span>{debouche}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Section avantages */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Pourquoi nous choisir ?
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {avantages.map((avantage, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.1 + index * 0.05 }}
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-purple-900/40 to-pink-900/40 p-6 rounded-lg backdrop-blur-sm border border-purple-500/30 hover:border-purple-400/50 transition-all"
              >
                <div className="flex items-center space-x-3">
                  <Heart className="w-5 h-5 text-pink-400" />
                  <p className="text-white font-medium">{avantage}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Section CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="text-center"
        >
          <div className="bg-gradient-to-br from-purple-900/40 to-pink-900/40 p-10 rounded-2xl backdrop-blur-sm border border-purple-500/30 max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-4">
              Prêt à commencer votre formation ?
            </h2>
            <p className="text-gray-300 mb-8">
              Rejoignez-nous et développez vos compétences dans un environnement professionnel et bienveillant.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                >
                  <span>Nous contacter</span>
                  <ArrowLeft className="w-4 h-4 rotate-180" />
                </motion.button>
              </Link>

              <motion.a
                href="https://www.cfa-montargis.com/"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 border border-white text-white hover:bg-white hover:text-slate-900 rounded-lg transition-colors flex items-center space-x-2"
              >
                <span>En savoir plus</span>
                <ExternalLink className="w-4 h-4" />
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default BeauteBienEtre;